/**
 * Loads the Google Maps JavaScript API on demand (admin site editor only) and declares
 * the handful of types we use, so no @types package is needed.
 */

export interface LatLngLiteral {
  lat: number;
  lng: number;
}

interface LatLng {
  lat(): number;
  lng(): number;
}

export interface MapsEventListener {
  remove(): void;
}

interface MouseEvent {
  latLng: LatLng | null;
}

export interface GMap {
  setCenter(p: LatLngLiteral): void;
  panTo(p: LatLngLiteral): void;
  setZoom(z: number): void;
  getZoom(): number | undefined;
  addListener(event: "click", handler: (e: MouseEvent) => void): MapsEventListener;
}

export interface GMarker {
  setPosition(p: LatLngLiteral): void;
  getPosition(): LatLng | null | undefined;
  setMap(map: GMap | null): void;
  addListener(event: "dragend" | "drag", handler: () => void): MapsEventListener;
}

export interface GCircle {
  setCenter(p: LatLngLiteral): void;
  setRadius(m: number): void;
  setMap(map: GMap | null): void;
}

export interface GoogleMapsApi {
  Map: new (
    el: HTMLElement,
    opts: {
      center: LatLngLiteral;
      zoom: number;
      mapTypeControl?: boolean;
      streetViewControl?: boolean;
      fullscreenControl?: boolean;
      clickableIcons?: boolean;
      gestureHandling?: "cooperative" | "greedy" | "auto";
    },
  ) => GMap;
  Marker: new (opts: { position: LatLngLiteral; map: GMap; draggable?: boolean; title?: string }) => GMarker;
  Circle: new (opts: {
    center: LatLngLiteral;
    radius: number;
    map: GMap;
    clickable?: boolean;
    strokeColor?: string;
    strokeOpacity?: number;
    strokeWeight?: number;
    fillColor?: string;
    fillOpacity?: number;
  }) => GCircle;
}

declare global {
  interface Window {
    google?: { maps?: GoogleMapsApi };
    __timetrackerMapsReady?: () => void;
  }
}

let loading: Promise<GoogleMapsApi> | null = null;

/** Loads the Maps JS API once per page; later calls share the same promise. */
export function loadGoogleMaps(apiKey: string): Promise<GoogleMapsApi> {
  if (window.google?.maps?.Map) return Promise.resolve(window.google.maps);
  if (loading) return loading;
  loading = new Promise<GoogleMapsApi>((resolve, reject) => {
    window.__timetrackerMapsReady = () => {
      const maps = window.google?.maps;
      if (maps) resolve(maps);
      else reject(new Error("Google Maps didn't load."));
    };
    const script = document.createElement("script");
    const params = new URLSearchParams({
      key: apiKey,
      v: "weekly",
      callback: "__timetrackerMapsReady",
      region: "GB",
      language: "en-GB",
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
    script.async = true;
    script.onerror = () => {
      loading = null; // allow a retry later
      script.remove();
      reject(new Error("Couldn't load Google Maps. Check your connection and the API key."));
    };
    document.head.appendChild(script);
  });
  return loading;
}

/** A plain link to a point on Google Maps (no API key needed). */
export const googleMapsLink = (lat: number, lng: number) =>
  `https://www.google.com/maps?q=${lat.toFixed(6)},${lng.toFixed(6)}`;
